import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Statistic, Button, Alert, Space, Typography, message, Modal, Form, Input, InputNumber, Select, Spin, Table, Tag } from 'antd';
import { WarningOutlined, ReloadOutlined, GiftOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

// Thresholds espelham os defaults de backend/utils/communityFinanceHealthMonitor.js
// (HEALTH_STUCK_BATCH_THRESHOLD, HEALTH_FAILED_NOTIFS_THRESHOLD,
// HEALTH_EXPIRING_MP_THRESHOLD) — usados so pra destaque visual dos cards,
// nao mudam o alerta real (que roda no backend via cron/run-now).
const THR_STUCK = 1;
const THR_FAILED = 10;
const THR_EXPIRING = 3;

export default function CommunityFinanceHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [grantModal, setGrantModal] = useState(false);
  const [granting, setGranting] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);
  const [teamSearchLoading, setTeamSearchLoading] = useState(false);
  const [grantForm] = Form.useForm();
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);

  const loadGrants = useCallback(async () => {
    setGrantsLoading(true);
    try {
      const r = await api.get('/admin/community-finance/credit-grants', { params: { limit: 100 } });
      setGrants(r.data.items || []);
    } catch (e) { /* silent */ } finally { setGrantsLoading(false); }
  }, []);
  useEffect(() => { loadGrants(); }, [loadGrants]);

  const searchTeams = async (q) => {
    if (!q || q.length < 2) return setTeamOptions([]);
    setTeamSearchLoading(true);
    try {
      const r = await api.get('/admin/community-finance/team-search', { params: { q } });
      setTeamOptions((r.data.items || []).map(t => ({
        value: t.id,
        label: `${t.name}${t.owner_name ? ' · ' + t.owner_name : ''}`,
      })));
    } catch { /* silent */ } finally { setTeamSearchLoading(false); }
  };

  const grantCredits = async () => {
    try {
      const v = await grantForm.validateFields();
      setGranting(true);
      await api.post('/admin/community-finance/grant-credits', {
        community_team_id: v.team_id,
        credits: v.credits,
        reason: v.reason,
      });
      message.success(`+${v.credits} créditos adicionados`);
      grantForm.resetFields();
      setGrantModal(false);
      loadGrants();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Falhou adicionar');
    } finally { setGranting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/community-finance/health');
      setData(r.data);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const it = setInterval(load, 60000);
    return () => clearInterval(it);
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/admin/community-finance/health/run-now');
      setData({ metrics: r.data.metrics, checked_at: r.data.checked_at || new Date().toISOString() });
      if (r.data.alerts?.length > 0) {
        message.warning(`${r.data.alerts.length} alerta(s) disparado(s)`);
      } else {
        message.success('Check limpo, sem alertas');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou rodar check');
    } finally {
      setRunning(false);
    }
  };

  if (!data) {
    return (
      <div style={{ padding: 40 }}>
        <Text type="secondary">{loading ? 'Carregando…' : 'Sem dados'}</Text>
      </div>
    );
  }

  const m = data.metrics || {};
  const failedByChannel = m.failed_by_channel || {};
  const hasAlerts =
    m.stuck_batches > THR_STUCK ||
    m.failed_notifications_24h > THR_FAILED ||
    m.mp_connections_expiring_soon > THR_EXPIRING ||
    m.paid_charges_without_paid_at > 0;

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 16]}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>Health — Financeiro Comunidade</Title>
          <Text type="secondary">
            Últ. check: {data.checked_at ? dayjs(data.checked_at).format('DD/MM HH:mm:ss') : '—'}
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<GiftOutlined />} onClick={() => setGrantModal(true)}>
              Adicionar créditos pro time
            </Button>
            <Button type="primary" icon={<ReloadOutlined />} loading={running} onClick={runNow}>
              Rodar check agora
            </Button>
          </Space>
        </Col>
      </Row>

      <Modal
        open={grantModal}
        title="Adicionar créditos pro time"
        onCancel={() => setGrantModal(false)}
        onOk={grantCredits}
        okText="Adicionar"
        okButtonProps={{ loading: granting }}
        destroyOnClose
      >
        <Form form={grantForm} layout="vertical" preserve={false}>
          <Form.Item label="Time" name="team_id"
            rules={[{ required: true, message: 'Escolhe o time' }]}>
            <Select
              showSearch
              placeholder="Buscar por nome do time (min 2 chars)"
              options={teamOptions}
              onSearch={searchTeams}
              filterOption={false}
              notFoundContent={teamSearchLoading ? <Spin size="small" /> : 'Digite pra buscar'}
            />
          </Form.Item>
          <Form.Item label="Quantos créditos"
            name="credits"
            rules={[{ required: true, message: 'Informe quantidade' }]}
            initialValue={20}>
            <InputNumber min={1} max={5000} style={{ width: '100%' }} placeholder="Ex: 20" />
          </Form.Item>
          <Form.Item label="Motivo (fica no log)" name="reason"
            rules={[{ required: true, message: 'Descreve o motivo' }]}>
            <Input.TextArea rows={2} maxLength={200} showCount
              placeholder="Ex: cortesia teste beta / suporte / compensação bug" />
          </Form.Item>
          <Alert type="info" showIcon style={{ fontSize: 12 }}
            message="Créditos são adicionados na hora sem cobrar nada. Ficam registrados como 'granted' pra rastreamento." />
        </Form>
      </Modal>

      {hasAlerts && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Alertas ativos — checar métricas destacadas abaixo"
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="Batches travados"
              value={m.stuck_batches ?? 0}
              valueStyle={{ color: m.stuck_batches > THR_STUCK ? '#dc2626' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="Notifications failed (24h)"
              value={m.failed_notifications_24h ?? 0}
              valueStyle={{ color: m.failed_notifications_24h > THR_FAILED ? '#dc2626' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="MP prestes a expirar"
              value={m.mp_connections_expiring_soon ?? 0}
              valueStyle={{ color: m.mp_connections_expiring_soon > THR_EXPIRING ? '#dc2626' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="Paid sem paid_at (webhook falhou)"
              value={m.paid_charges_without_paid_at ?? 0}
              valueStyle={{ color: m.paid_charges_without_paid_at > 0 ? '#dc2626' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {Object.keys(failedByChannel).length > 0 && (
        <Card style={{ marginTop: 16 }} title="Failed por canal (24h)">
          <Space direction="vertical">
            {Object.entries(failedByChannel).map(([ch, n]) => (
              <Text key={ch}>{ch}: <strong>{n}</strong></Text>
            ))}
          </Space>
        </Card>
      )}

      <Card
        style={{ marginTop: 16 }}
        title={<Space><Text strong>Créditos cortesia (últimos 100)</Text><Tag>{grants.length}</Tag></Space>}
        extra={
          <Button size="small" icon={<ReloadOutlined />} onClick={loadGrants} loading={grantsLoading} />
        }
      >
        <Table
          rowKey="id"
          size="small"
          loading={grantsLoading}
          dataSource={grants}
          pagination={{ pageSize: 20, simple: true, size: 'small' }}
          scroll={{ x: 600 }}
          columns={[
            { title: 'Data', dataIndex: 'created_at', width: 130,
              render: v => v ? dayjs(v).format('DD/MM/YY HH:mm') : '—' },
            { title: 'Time', dataIndex: 'team_name',
              render: (v, r) => <Text strong>{v || `#${r.community_team_id}`}</Text> },
            { title: 'Adicionados', dataIndex: 'credits', width: 110, align: 'right',
              render: v => <Tag color="purple" style={{ fontSize: 12 }}>+{v}</Tag> },
            { title: 'Saldo atual', dataIndex: 'current_balance', width: 110, align: 'right',
              render: v => <Text strong style={{ color: '#128C7E' }}>{v}</Text> },
          ]}
        />
      </Card>
    </div>
  );
}
